/**
 * Pick Your Favorites
 * Cart Controller
 *
 * Responsibilities:
 * - add completed candy bags to Shopify
 * - refresh and open the Dawn cart drawer
 * - read completed PYF bags already in the cart
 * - reserve candy inventory across multiple bags
 */

window.PYF = window.PYF || {};

PYF.cartReservations =
  PYF.cartReservations || {};

/* =====================================================
   EDIT BAG STATE
===================================================== */

PYF.editingBag =
  PYF.editingBag || null;

/* =====================================================
   CART ROOT
===================================================== */

PYF.getCartRoot = function() {
  if (
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
  ) {
    return window.Shopify.routes.root;
  }

  return "/";
};


/* =====================================================
   UNIQUE BAG ID
===================================================== */

PYF.createBagGroupId = function() {
  return (
    "pyf-" +
    Date.now() +
    "-" +
    Math.random()
      .toString(36)
      .slice(2, 9)
  );
};


/* =====================================================
   CART RESERVATION HELPERS
===================================================== */

/**
 * Determine whether a Shopify cart line belongs
 * to a completed Pick Your Favorites bag.
 */
PYF.isPYFCartItem = function(item) {
  if (!item || !item.properties) {
    return false;
  }

  return Boolean(
    item.properties._pyf_bag_id
  );
};


/**
 * Return how many units of a candy variant are
 * already reserved in completed bags in the cart.
 */
PYF.getReservedInventory = function(
  variantId
) {
  const key =
    String(variantId || "");

  if (!key) {
    return 0;
  }

  return Math.max(
    0,
    Number(
      PYF.cartReservations[key]
    ) || 0
  );
};


/**
 * Build a reservation map from Shopify cart data.
 *
 */
PYF.setCartReservationsFromCart =
  function(cart) {
    const reservations = {};

    const cartItems =
      cart &&
      Array.isArray(cart.items)
        ? cart.items
        : [];

    cartItems.forEach(function(item) {
      if (
        !PYF.isPYFCartItem(item)
      ) {
        return;
      }

      const properties =
        item.properties || {};

      /*
       * NEW SINGLE-BAG FORMAT
       *
       * The Shopify cart line is the Pick & Mix Bag,
       * while the selected candy variant IDs and
       * quantities are stored in this hidden property.
       */
      const encodedReservations =
        properties
          ._pyf_candy_reservations;

      if (encodedReservations) {
        try {
          const candyItems =
            typeof encodedReservations ===
            "string"
              ? JSON.parse(
                  encodedReservations
                )
              : encodedReservations;

          if (
            Array.isArray(candyItems)
          ) {
            candyItems.forEach(
              function(candy) {
                const variantId =
                  String(
                    candy.variantId ||
                    ""
                  );

                const quantity =
                  Math.max(
                    0,
                    Number.parseInt(
                      candy.quantity,
                      10
                    ) || 0
                  );

                if (
                  !variantId ||
                  quantity <= 0
                ) {
                  return;
                }

                reservations[variantId] =
                  (
                    reservations[
                      variantId
                    ] || 0
                  ) + quantity;
              }
            );

            return;
          }
        } catch (error) {
          console.error(
            "PYF could not read candy reservations from the bag cart item:",
            error,
            item
          );
        }
      }

      /*
       * OLD MULTI-CANDY FORMAT
       *
       * Keep this fallback temporarily so bags already
       * in the customer's cart still reserve inventory.
       */
      const variantId =
        String(
          item.variant_id ||
          item.id ||
          ""
        );

      const quantity =
        Math.max(
          0,
          Number.parseInt(
            item.quantity,
            10
          ) || 0
        );

      if (
        !variantId ||
        quantity <= 0
      ) {
        return;
      }

      reservations[variantId] =
        (
          reservations[variantId] ||
          0
        ) + quantity;
    });

    PYF.cartReservations =
      reservations;

    document.dispatchEvent(
      new CustomEvent(
        "pyf:cart-reservations-changed",
        {
          detail: {
            reservations: {
              ...reservations
            },

            cart: cart || null
          }
        }
      )
    );

    return reservations;
  };


/**
 * Fetch the current Shopify cart and refresh
 * the candy reservation map.
 */
PYF.refreshCartReservations =
  async function(cartOverride) {
    const cart =
      cartOverride ||
      await PYF.fetchCart();

    PYF.setCartReservationsFromCart(
      cart
    );

    return cart;
  };


/* =====================================================
   VALIDATE BAG
===================================================== */

PYF.validateBagForCart = function() {
  if (!PYF.state) {
    return {
      success: false,
      message:
        "The candy builder is not ready."
    };
  }

  const selectedItems =
    typeof PYF.getSelectedItems ===
    "function"
      ? PYF.getSelectedItems()
      : [];

  if (!selectedItems.length) {
    return {
      success: false,
      message:
        "Choose your candies before adding the bag to the cart."
    };
  }

  if (
    typeof PYF.isBagFull !==
      "function" ||
    !PYF.isBagFull()
  ) {
    const remaining =
      typeof PYF.getRemainingPicks ===
      "function"
        ? PYF.getRemainingPicks()
        : 0;

    return {
      success: false,

      message:
        remaining === 1
          ? "Add 1 more pick to complete your bag."
          : `Add ${remaining} more picks to complete your bag.`
    };
  }

  const invalidItem =
    selectedItems.find(
      function(item) {
        const variantId =
          Number(item.variantId);

        const quantity =
          Number(item.quantity);

        return (
          !Number.isInteger(
            variantId
          ) ||
          variantId <= 0 ||
          !Number.isInteger(
            quantity
          ) ||
          quantity <= 0
        );
      }
    );

  if (invalidItem) {
    console.error(
      "PYF invalid cart item:",
      invalidItem
    );

    return {
      success: false,

      message:
        "One of the selected candies is missing its Shopify variant ID."
    };
  }

  return {
    success: true,
    selectedItems: selectedItems
  };
};


/* =====================================================
   BUILD CART ITEMS
===================================================== */

PYF.buildCartItems = function() {
  const selectedItems =
    PYF.getSelectedItems();

  const bagGroupId =
    PYF.createBagGroupId();

  const bagSize =
    String(
      PYF.state.bag.size || ""
    );

  const totalPicks =
    Number(
      PYF.state.currentPicks
    ) || 0;

  const maxPicks =
    Number(
      PYF.state.bag.maxPicks
    ) || 0;

  const bagSubtotal =
    typeof PYF.getSubtotal ===
    "function"
      ? PYF.getSubtotal()
      : 0;

  const bagTotal =
    typeof PYF.getTotal ===
    "function"
      ? PYF.getTotal()
      : bagSubtotal;

  const mixSummary =
    typeof PYF.getMixSummary ===
    "function"
      ? PYF.getMixSummary()
      : "";

  const createdAt =
    new Date().toISOString();

  const items =
    selectedItems.map(
      function(item) {
        return {
          id:
            Number(
              item.variantId
            ),

          quantity:
            Number(
              item.quantity
            ),

          properties: {
            _pyf_bag_id:
              bagGroupId,

            _pyf_bag_size:
              bagSize,

            _pyf_total_picks:
              String(
                totalPicks
              ),

            _pyf_max_picks:
              String(
                maxPicks
              ),

            _pyf_price_per_pick:
              Number(
                PYF.state
                  .pricePerPick || 0
              ).toFixed(2),

            _pyf_bag_subtotal:
              Number(
                bagSubtotal
              ).toFixed(2),

            _pyf_bag_total:
              Number(
                bagTotal
              ).toFixed(2),

            _pyf_mix_summary:
              mixSummary,

            _pyf_created_at:
              createdAt
          }
        };
      }
    );

  return {
    bagGroupId: bagGroupId,
    items: items
  };
};

/* =====================================================
   BUILD SINGLE BAG CART ITEM
===================================================== */

PYF.buildBagCartItem = function() {
  const selectedItems =
    typeof PYF.getSelectedItems ===
    "function"
      ? PYF.getSelectedItems()
      : [];

  const bag =
    PYF.state &&
    PYF.state.bag
      ? PYF.state.bag
      : null;

  const bagVariantId =
    bag
      ? Number(bag.variantId)
      : 0;

  if (
    !Number.isInteger(bagVariantId) ||
    bagVariantId <= 0
  ) {
    console.error(
      "PYF.buildBagCartItem: Missing or invalid bag variant ID.",
      bag
    );

    return null;
  }

  if (!selectedItems.length) {
    console.error(
      "PYF.buildBagCartItem: No candies selected."
    );

    return null;
  }

  const bagGroupId =
    PYF.createBagGroupId();

  const bagSize =
    String(
      bag.size || ""
    );

  const totalPicks =
    Number(
      PYF.state.currentPicks
    ) || 0;

  const maxPicks =
    Number(
      bag.maxPicks
    ) || 0;

  const mixSummary =
    typeof PYF.getMixSummary ===
    "function"
      ? PYF.getMixSummary()
      : "";

  /*
   * Store the individual candy variant IDs and
   * quantities for our custom inventory system.
   */
  const candyReservations =
    selectedItems.map(
      function(item) {
        return {
          variantId:
            String(
              item.variantId
            ),

          quantity:
            Number(
              item.quantity
            ) || 0,

          title:
            String(
              item.title || "Candy"
            )
        };
      }
    );

  return {
    id: bagVariantId,

    quantity: 1,

    properties: {
      /*
       * Visible customer-facing properties.
       */

      
        mixSummary,

      "Picks":
        String(totalPicks),

      /*
       * Hidden internal properties.
       * Shopify hides properties beginning
       * with an underscore in most themes.
       */
      _pyf_bag_id:
        bagGroupId,

      _pyf_bag_size:
        bagSize,

      _pyf_total_picks:
        String(totalPicks),

      _pyf_max_picks:
        String(maxPicks),

      _pyf_candy_reservations:
        JSON.stringify(
          candyReservations
        ),

      _pyf_created_at:
        new Date().toISOString()
    }
  };
};

/* =====================================================
   CART BUTTON
===================================================== */

PYF.getCartButton = function() {
  const sidebar =
    document.querySelector(
      ".pyf-sidebar"
    );

  if (!sidebar) {
    return null;
  }

  return (
    sidebar.querySelector(
      "#pyf-add-bag"
    ) ||
    sidebar.querySelector(
      ".pyf-add-bag-button"
    ) ||
    sidebar.querySelector(
      ".pyf-checkout-button"
    )
  );
};


PYF.setCartButtonLoading =
  function(isLoading) {
    const button =
      PYF.getCartButton();

    if (!button) {
      return;
    }

    if (
      !button.dataset.defaultText
    ) {
      button.dataset.defaultText =
        button.textContent.trim() ||
        "Add Bag to Cart";
    }

    button.classList.toggle(
      "loading",
      Boolean(isLoading)
    );

    if (isLoading) {
      button.disabled = true;

      button.setAttribute(
        "aria-busy",
        "true"
      );

      button.textContent =
        "Adding Bag…";

      return;
    }

    button.removeAttribute(
      "aria-busy"
    );

    if (
      typeof PYF.updateSidebar ===
      "function"
    ) {
      PYF.updateSidebar();
    } else {
      button.textContent =
        button.dataset.defaultText;
    }
  };


/* =====================================================
   MESSAGE
===================================================== */

PYF.showCartMessage = function(
  title,
  message
) {
  if (
    typeof PYF.showToast ===
    "function"
  ) {
    PYF.showToast(
      title,
      message
    );

    return;
  }

  alert(
    `${title}\n\n${message}`
  );
};


/* =====================================================
   ADD ITEMS TO SHOPIFY
===================================================== */

PYF.sendCartItems =
  async function(items) {
    const root =
      PYF.getCartRoot();

    const response =
      await fetch(
        `${root}cart/add.js`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json",

            "X-Requested-With":
              "XMLHttpRequest"
          },

          body:
            JSON.stringify({
              items: items
            })
        }
      );

    let responseData = {};

    try {
      responseData =
        await response.json();
    } catch (error) {
      console.error(
        "PYF cart response was not valid JSON.",
        error
      );
    }

    if (!response.ok) {
      throw new Error(
        responseData.description ||
        responseData.message ||
        "Shopify could not add this bag to the cart."
      );
    }

    return responseData;
  };


/* =====================================================
   FETCH FRESH DAWN SECTIONS
===================================================== */

PYF.fetchFreshCartSections =
  async function() {
    const root =
      PYF.getCartRoot();

    const sectionNames =
      "cart-drawer,cart-icon-bubble";

    const sectionUrl =
      `${root}?sections=${encodeURIComponent(
        sectionNames
      )}`;

    const response =
      await fetch(
        sectionUrl,
        {
          method: "GET",

          headers: {
            Accept:
              "application/json",

            "X-Requested-With":
              "XMLHttpRequest"
          },

          cache: "no-store"
        }
      );

    if (!response.ok) {
      throw new Error(
        `Unable to refresh cart drawer: ${response.status}`
      );
    }

    return response.json();
  };


/* =====================================================
   RENDER AND OPEN DRAWER
===================================================== */

PYF.refreshAndOpenCartDrawer =
  async function(firstItemId) {
    const cartDrawer =
      document.querySelector(
        "cart-drawer"
      );

    if (
      !cartDrawer ||
      typeof cartDrawer
        .renderContents !==
        "function"
    ) {
      return false;
    }

    const sections =
      await PYF
        .fetchFreshCartSections();

    if (
      !sections ||
      !sections["cart-drawer"] ||
      !sections[
        "cart-icon-bubble"
      ]
    ) {
      throw new Error(
        "Shopify returned incomplete cart drawer sections."
      );
    }

    /*
     * Dawn marks an empty drawer with this class.
     * Remove it before and after rendering.
     */
    cartDrawer.classList.remove(
      "is-empty"
    );

    if (
      typeof cartDrawer
        .setActiveElement ===
      "function"
    ) {
      cartDrawer.setActiveElement(
        document.activeElement
      );
    }

    cartDrawer.renderContents({
      id:
        firstItemId || null,

      sections: {
        "cart-drawer":
          sections[
            "cart-drawer"
          ],

        "cart-icon-bubble":
          sections[
            "cart-icon-bubble"
          ]
      }
    });

    cartDrawer.classList.remove(
      "is-empty"
    );

    const drawerItems =
      cartDrawer.querySelector(
        "cart-drawer-items"
      );

    if (drawerItems) {
      drawerItems.classList.remove(
        "is-empty"
      );
    }

    return true;
  };


/* =====================================================
   FETCH CART
===================================================== */

PYF.fetchCart =
  async function() {
    const root =
      PYF.getCartRoot();

    const response =
      await fetch(
        `${root}cart.js`,
        {
          method: "GET",

          headers: {
            Accept:
              "application/json"
          },

          cache:
            "no-store"
        }
      );

    if (!response.ok) {
      throw new Error(
        `Unable to read cart: ${response.status}`
      );
    }

    return response.json();
  };


/* =====================================================
   FALLBACK CART COUNT
===================================================== */

PYF.updateCartCount = function(
  cart
) {
  if (!cart) {
    return;
  }

  const itemCount =
    Number(
      cart.item_count
    ) || 0;

  const cartLink =
    document.querySelector(
      "#cart-icon-bubble"
    );

  if (!cartLink) {
    return;
  }

  let bubble =
    cartLink.querySelector(
      ".cart-count-bubble"
    );

  if (itemCount <= 0) {
    if (bubble) {
      bubble.remove();
    }

    return;
  }

  if (!bubble) {
    bubble =
      document.createElement(
        "div"
      );

    bubble.className =
      "cart-count-bubble";

    bubble.innerHTML =
      '<span aria-hidden="true"></span>' +
      '<span class="visually-hidden"></span>';

    cartLink.appendChild(
      bubble
    );
  }

  const visibleCount =
    bubble.querySelector(
      "span[aria-hidden='true']"
    );

  const hiddenCount =
    bubble.querySelector(
      ".visually-hidden"
    );

  if (visibleCount) {
    visibleCount.textContent =
      itemCount < 100
        ? String(itemCount)
        : "";
  }

  if (hiddenCount) {
    hiddenCount.textContent =
      `${itemCount} items`;
  }
};


/* =====================================================
   ADD COMPLETE BAG
===================================================== */

PYF.addBagToCart =
  async function() {
    const validation =
      PYF.validateBagForCart();

    if (!validation.success) {
      PYF.showCartMessage(
        "Bag not ready",
        validation.message
      );

      return;
    }

    const bagCartItem =
  PYF.buildBagCartItem();

if (!bagCartItem) {
  PYF.showCartMessage(
    "Bag not ready",
    "The completed bag could not be prepared for the cart."
  );

  return;
}

const cartPayload = {
  bagGroupId:
    bagCartItem.properties
      ._pyf_bag_id,

  items: [
    bagCartItem
  ]
};

    PYF.setCartButtonLoading(
      true
    );

    try {
      const completedBagSize =
        PYF.state.bag.size;

      const addResponse =
        await PYF.sendCartItems(
          cartPayload.items
        );

      /*
       * Immediately read the updated cart so the
       * completed bag reserves its candy inventory
       * before the customer starts Bag #2.
       */
      const freshCart =
        await PYF
          .refreshCartReservations();

      const firstItemId =
        addResponse &&
        Array.isArray(
          addResponse.items
        ) &&
        addResponse.items.length
          ? addResponse.items[0].id
          : cartPayload.items[0].id;

      let drawerOpened = false;

      try {
        drawerOpened =
          await PYF
            .refreshAndOpenCartDrawer(
              firstItemId
            );
      } catch (drawerError) {
        console.error(
          "PYF cart drawer refresh failed:",
          drawerError
        );
      }

      if (!drawerOpened) {
        PYF.updateCartCount(
          freshCart
        );

        PYF.showCartMessage(
          "Bag added",
          `${completedBagSize} candy bag added to your cart.`
        );
      }

/* =====================================================
  * Reset only after Shopify confirms the add
  * and the reservation map has been updated.
 =====================================================*/

          const wasEditingBag =
          Boolean(PYF.editingBag);

          PYF.editingBag = null;

          PYF.resetItems();

        document.dispatchEvent(
        new CustomEvent(
          "pyf:bag-added",
          {
            detail: {
              bagGroupId:
                cartPayload
                  .bagGroupId,

              bagSize:
                completedBagSize,

              reservations: {
                ...PYF
                  .cartReservations
              },

              response:
                addResponse
            }
          }
        )
      );

    } catch (error) {
      console.error(
        "PYF.addBagToCart error:",
        error
      );

      PYF.showCartMessage(
        "Could not add bag",
        error.message ||
        "Please try again."
      );

    } finally {
      PYF.setCartButtonLoading(
        false
      );
    }
  };

  /* =====================================================
   REMOVE CART LINE FOR EDITING
===================================================== */

PYF.removeCartLineForEditing =
  async function(lineIndex) {

    const line =
      Number.parseInt(
        lineIndex,
        10
      );

    if (
      !Number.isInteger(line) ||
      line <= 0
    ) {
      throw new Error(
        "Invalid cart line."
      );
    }


    const root =
      PYF.getCartRoot();


    const response =
      await fetch(
        `${root}cart/change.js`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json",

            "X-Requested-With":
              "XMLHttpRequest"
          },

          body:
            JSON.stringify({
              line: line,
              quantity: 0
            })
        }
      );


    let cart = null;

    try {
      cart =
        await response.json();
    } catch (error) {
      console.error(
        "PYF edit bag: Invalid Shopify response.",
        error
      );
    }


    if (!response.ok) {
      throw new Error(
        cart?.description ||
        cart?.message ||
        "Shopify could not remove the bag."
      );
    }


    return cart;
  };


/* =====================================================
   CLOSE CART DRAWER
===================================================== */

PYF.closeCartDrawerForEditing =
  function() {

    const cartDrawer =
      document.querySelector(
        "cart-drawer"
      );

    if (
      cartDrawer &&
      typeof cartDrawer.close ===
        "function"
    ) {
      cartDrawer.close();
    }
  };


/* =====================================================
   SCROLL TO BUILDER
===================================================== */

PYF.scrollToBuilderForEditing =
  function() {

    const target =
      document.querySelector(
        ".pyf-bag-selector"
      ) ||
      document.querySelector(
        ".pyf-page"
      );

    if (!target) {
      return;
    }


    window.setTimeout(
      function() {

        target.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });

      },
      150
    );
  };


/* =====================================================
   EDIT COMPLETED BAG
===================================================== */

PYF.editCompletedBag =
  async function(button) {

    if (!button) {
      return;
    }


    if (
      button.dataset.pyfEditBusy ===
      "true"
    ) {
      return;
    }


    /*
     * Protect an unfinished bag that may
     * already be in the builder.
     */

    const currentPicks =
      Number(
        PYF.state?.currentPicks
      ) || 0;


    if (currentPicks > 0) {

      const continueEditing =
        window.confirm(
          "You currently have an unfinished candy bag. Editing this cart bag will replace those selections. Continue?"
        );


      if (!continueEditing) {
        return;
      }
    }


    const lineIndex =
      button.dataset.lineIndex;


    const bagSize =
      button.dataset.bagSize ||
      "½ lb";


    const maxPicks =
      Number.parseInt(
        button.dataset.maxPicks,
        10
      ) || 8;


    const bagVariantId =
      button.dataset.bagVariantId ||
      "";


    let candyReservations = [];


    try {

      candyReservations =
        JSON.parse(
          button.dataset
            .candyReservations ||
          "[]"
        );

    } catch (error) {

      console.error(
        "PYF edit bag: Could not read saved candy selections.",
        error
      );


      PYF.showCartMessage(
        "Could not edit bag",
        "The saved candy selections could not be read."
      );

      return;
    }


    if (
      !Array.isArray(
        candyReservations
      ) ||
      !candyReservations.length
    ) {

      PYF.showCartMessage(
        "Could not edit bag",
        "No saved candy selections were found for this bag."
      );

      return;
    }


    button.dataset.pyfEditBusy =
      "true";

    button.disabled = true;

    const originalText =
      button.textContent;

    button.textContent =
      "Loading…";


    try {

      /*
       * 1. Remove the completed bag from
       *    Shopify first.
       *
       * This releases its candy inventory.
       */

      const cart =
        await PYF
          .removeCartLineForEditing(
            lineIndex
          );


      /*
       * 2. Immediately rebuild inventory
       *    reservations from the updated cart.
       */

      if (
        typeof PYF
          .setCartReservationsFromCart ===
        "function"
      ) {

        PYF.setCartReservationsFromCart(
          cart
        );
      }


      /*
       * 3. Update visible Dawn cart count.
       */

      if (
        typeof PYF.updateCartCount ===
        "function"
      ) {

        PYF.updateCartCount(
          cart
        );
      }


      /*
       * 4. Restore the bag into the builder.
       */

      if (
        typeof PYF
          .restoreBagForEditing !==
        "function"
      ) {

        throw new Error(
          "Bag restore function is not available."
        );
      }


      const restoreResult =
        PYF.restoreBagForEditing({
          bagSize:
            bagSize,

          maxPicks:
            maxPicks,

          bagVariantId:
            bagVariantId,

          candyReservations:
            candyReservations
        });

        /*
        * Remember that this bag is being edited.
        */

        PYF.editingBag = {
        bagSize: bagSize,
        maxPicks: maxPicks,
        bagVariantId: bagVariantId
       };

      if (
        !restoreResult ||
        restoreResult.success ===
          false
      ) {

        throw new Error(
          "The bag could not be restored."
        );
      }


      /*
       * 5. Close cart and bring customer
       *    back to the builder.
       */

      PYF.closeCartDrawerForEditing();

      PYF.scrollToBuilderForEditing();


      PYF.showCartMessage(
        "Bag ready to edit",
        `${bagSize} bag restored. Make your changes and add it back to the cart when you're finished.`
      );


      document.dispatchEvent(
        new CustomEvent(
          "pyf:bag-edit-started",
          {
            detail: {
              bagSize:
                bagSize,

              maxPicks:
                maxPicks,

              restoredPicks:
                restoreResult
                  .restoredPicks
            }
          }
        )
      );


    } catch (error) {

      console.error(
        "PYF.editCompletedBag error:",
        error
      );


      PYF.showCartMessage(
        "Could not edit bag",
        error.message ||
        "Please try again."
      );


      button.dataset.pyfEditBusy =
        "false";

      button.disabled = false;

      button.textContent =
        originalText;
    }
  };


/* =====================================================
   EDIT BAG BUTTON LISTENER
===================================================== */

/*
 * Use event delegation because Dawn replaces
 * the cart drawer HTML whenever the cart updates.
 */

document.addEventListener(
  "click",
  function(event) {

    const button =
      event.target.closest(
        "[data-pyf-edit-bag]"
      );


    if (!button) {
      return;
    }


    event.preventDefault();

    event.stopPropagation();


    PYF.editCompletedBag(
      button
    );
  }
);

/* =====================================================
   WATCH DAWN CART CHANGES
===================================================== */

PYF.subscribeToCartUpdates =
  function() {
    if (
      PYF.cartUpdateSubscription
    ) {
      return;
    }

    if (
      typeof window.subscribe !==
        "function" ||
      !window.PUB_SUB_EVENTS ||
      !window.PUB_SUB_EVENTS
        .cartUpdate
    ) {
      return;
    }

    PYF.cartUpdateSubscription =
      window.subscribe(
        window.PUB_SUB_EVENTS
          .cartUpdate,
        function() {
          PYF
            .refreshCartReservations()
            .catch(
              function(error) {
                console.error(
                  "PYF could not refresh reservations after a cart update:",
                  error
                );
              }
            );
        }
      );
  };


/* =====================================================
   INITIALIZE CART
===================================================== */

PYF.initCart = function() {
  /*
   * Read existing completed bags whenever the
   * builder loads.
   */
  PYF
    .refreshCartReservations()
    .catch(function(error) {
      console.error(
        "PYF could not read existing cart reservations:",
        error
      );
    });

  PYF.subscribeToCartUpdates();

  const button =
    PYF.getCartButton();

  if (!button) {
    console.warn(
      "PYF.initCart: Add Bag button not found."
    );

    return;
  }

  if (
    button.dataset
      .pyfCartInitialized ===
    "true"
  ) {
    return;
  }

  button.dataset
    .pyfCartInitialized =
    "true";

  button.type =
    "button";

  button.addEventListener(
    "click",
    function(event) {
      event.preventDefault();

      if (
        button.disabled ||
        button.getAttribute(
          "aria-busy"
        ) === "true"
      ) {
        return;
      }

      PYF.addBagToCart();
    }
  );

  console.log(
    "🍬 PYF Cart Initialized"
  );
};


/* =====================================================
   PAGE / WINDOW REFRESHES
===================================================== */

window.addEventListener(
  "pageshow",
  function() {
    if (
      typeof PYF.fetchCart !==
      "function"
    ) {
      return;
    }

    PYF
      .refreshCartReservations()
      .catch(function(error) {
        console.error(
          "PYF reservation refresh failed:",
          error
        );
      });
  }
);